/* Copyright 2013 - 2024 Waiterio LLC */
import getBrowserLanguage from '@monorepool/shared/getBrowserLanguage.js'
import getPhrases from '@multilocale/client/getPhrases.js'
import { useSyncExternalStore } from 'react'
import serializeJsonForScript from './serializeJsonForScript.js'

let defaultLanguage
const defaultLanguageListeners = new Set()
let defaultLanguageNotificationQueued = false
let organizationId
let project
let dictionaries = {}
let loading
let error
let suspender
let fetchedLanguages = {}

export const init = params => {
  let cache = {}
  if (typeof window !== 'undefined' && window.__MULTILOCALE_CACHE__) {
    cache = window.__MULTILOCALE_CACHE__
  }

  // Server-side: each request may target a different project (one per
  // subdomain in a whitelabel setup). Reset module-level state so translations
  // from a previous request in the same warm Lambda don't leak.
  if (typeof window === 'undefined') {
    dictionaries = {}
    fetchedLanguages = {}
    suspender = undefined
  }

  defaultLanguage = params.defaultLanguage
  organizationId = params.organizationId
  project = params.project

  if (params.dictionaries) {
    dictionaries = params.dictionaries
  } else if (cache?.dictionaries && Object.keys(dictionaries).length === 0) {
    dictionaries = cache.dictionaries
  }
}

export const setDictionaries = dictionaries_ => {
  dictionaries = dictionaries_
}

export const getDefaultLanguage = () => defaultLanguage

const subscribeDefaultLanguage = listener => {
  defaultLanguageListeners.add(listener)

  return () => defaultLanguageListeners.delete(listener)
}

const notifyDefaultLanguageListeners = () => {
  defaultLanguageNotificationQueued = false
  defaultLanguageListeners.forEach(listener => listener())
}

export const setDefaultLanguage = defaultLanguage_ => {
  if (defaultLanguage === defaultLanguage_) {
    return
  }

  defaultLanguage = defaultLanguage_

  // LanguageSetter components run before the rest of the app and historically
  // called this function during render. Defer the notification until that
  // render has committed; subscribers still read the new snapshot immediately,
  // and every already-mounted translation consumer gets a follow-up render.
  if (typeof window !== 'undefined' && !defaultLanguageNotificationQueued) {
    defaultLanguageNotificationQueued = true
    queueMicrotask(notifyDefaultLanguageListeners)
  }
}

export const initMultilocale = init

export const useTranslation = forcedLanguage => {
  // Some callers are not React components: the default document templates
  // (defaultQuoteTemplate.js, defaultInvoiceTemplate.js, …) call this from
  // plain functions to translate a template they build outside a render, and
  // always pass an explicit language. There is no dispatcher there, so the
  // hook throws. Fall back to a plain read — that is exactly what those call
  // sites got before the subscription was introduced, and the value is
  // discarded anyway whenever `forcedLanguage` is set. Inside a component the
  // hook never throws, so hook order stays unconditional and already-mounted
  // consumers still re-render on setDefaultLanguage().
  let reactiveDefaultLanguage

  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    reactiveDefaultLanguage = useSyncExternalStore(
      subscribeDefaultLanguage,
      getDefaultLanguage,
      getDefaultLanguage,
    )
  } catch {
    reactiveDefaultLanguage = getDefaultLanguage()
  }

  let language =
    forcedLanguage || reactiveDefaultLanguage || getBrowserLanguage() || 'en'

  let isBrowser = typeof window !== 'undefined'
  let isDictionaryEmpty =
    typeof dictionaries[language] === 'undefined' ||
    (isBrowser &&
      dictionaries[language] &&
      Object.keys(dictionaries[language]).length === 0)

  if (
    organizationId &&
    project &&
    !suspender &&
    isDictionaryEmpty &&
    !fetchedLanguages[language]
  ) {
    loading = true
    // Race the fetch against a timeout. A hung connection (opens but never
    // responds) would otherwise leave `suspender` pending forever — and because
    // `loading` is module-level, EVERY component's t() then throws that pending
    // promise and the whole app freezes on the Suspense fallback, even when the
    // phrases were already loaded for the visible language. On timeout we fall
    // through to the catch below so "the show goes on".
    let timeoutId
    suspender = Promise.race([
      getPhrases(
        {
          organizationId,
          language,
          project,
          fields: ['key', 'value', 'language'],
        },
        {
          retries: 1,
        },
      ),
      new Promise((resolve, reject) => {
        timeoutId = setTimeout(
          () => reject(new Error(`getPhrases timed out for ${language}`)),
          15000,
        )
      }),
    ])
      .then(phrases => {
        clearTimeout(timeoutId)
        if (!dictionaries[language]) {
          dictionaries[language] = {}
        }

        phrases.forEach(phrase => {
          if (!dictionaries[phrase.language]) {
            dictionaries[phrase.language] = {}
          }
          dictionaries[phrase.language][phrase.key] = phrase.value
        })
        fetchedLanguages[language] = true
        suspender = null
        loading = false
      })
      .catch(error_ => {
        clearTimeout(timeoutId)
        error = error_
        console.error(error)
        fetchedLanguages[language] = true
        suspender = null
        loading = false
        // THE SHOW MUST GO ON — keep any phrases already loaded for this
        // language instead of wiping them on a spurious timeout.
        if (!dictionaries[language]) {
          dictionaries[language] = {}
        }
        return null
      })
  }

  if (loading) {
    throw suspender
    // THE SHOW MUST GO ON
    // } else if (error) {
    // throw error
  } else {
    const translateString = (key, fallback) => {
      let value = ''

      if (dictionaries?.[language]?.[key]) {
        value = dictionaries[language][key]
      } else if (fallback?.length >= 0) {
        value = fallback
      } else if (!(key?.startsWith('__') && key?.endsWith('__'))) {
        value = key
      }

      return value
    }

    const translateError = (error, fallback) => {
      let message = error?.toString?.()
      message = message?.length ? message : fallback || 'An error occurred'
      message = translateString(message, fallback)

      if (error?.variables) {
        Object.keys(error.variables || {}).forEach(variable => {
          message = message.replace(
            `{${variable}}`,
            translateString(error.variables[variable]),
          )
        })
      }

      message += error?.cause?.message
        ? `:\n${translateString(error.cause.message)}`
        : ''

      return message
    }

    const translate = (parameter1, fallback) => {
      if (parameter1 && parameter1 instanceof Error) {
        return translateError(parameter1, fallback)
      }
      if (parameter1 && typeof parameter1 === 'string') {
        return translateString(parameter1, fallback)
      }
      return fallback
    }

    const Cache = () => {
      const windowVariables = `window.__MULTILOCALE_CACHE__ = ${serializeJsonForScript(
        {
          dictionaries: {
            [language]: dictionaries[language],
          },
        },
      )};`
      return <script dangerouslySetInnerHTML={{ __html: windowVariables }} />
    }

    return {
      language,
      t: translate,
      translate,
      Cache,
      MultilocaleCache: Cache,
    }
  }
}

export default useTranslation
